import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { i18n } from "../../translate/i18n";

function IconChevronDown({ open }) {
	return (
		<svg
			className={`size-4 shrink-0 text-default-500 transition-transform${open ? " rotate-180" : ""}`}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden
		>
			<polyline points="6 9 12 15 18 9" />
		</svg>
	);
}

function Badge({ item, onRemove,exibicao }) {
	const color = item.color || "#64748b";
	const appearance =
		typeof exibicao === "function"
			? exibicao(item)
			: null;

	return (
		<button
			type="button"
			className="tailwick-tags-multi__badge"
			style={{
				backgroundColor: `${color}14`,
				color,
				borderColor: `${color}40`,
			}}
			onClick={() => onRemove(item.id)}
			title={item.name}
		>
			{appearance ? appearance : <span className="tailwick-tags-multi__badge-dot" style={{ backgroundColor: color }} />}
			<span className="tailwick-tags-multi__badge-label">{item.name}</span>
			<span className="tailwick-tags-multi__badge-remove">×</span>
		</button>
	);
}

function ModalMultiSelector({
	title = "",
	placeholder = "",
	selected = [],
	setSelect,
	itens = [],
	handleSelect,
	handleDeselect,
	menuPlacement = "dropdown",
	exibicao,
}) {

	const containerRef = useRef(null);

	const selectedItems = useMemo(
		() => itens.filter((item) => Array.isArray(item.id) ? item.id.some((id) => selected.indexOf(id) > -1) : selected.indexOf(item.id) > -1),
		[selected, itens]
	);
	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		if (menuPlacement === "dropdown") {
			document.addEventListener("mousedown", handleClickOutside);
			return () => document.removeEventListener("mousedown", handleClickOutside);
		}

		return undefined;
	}, [open, menuPlacement]);

	const summary =
		selectedItems.length === 0
			? placeholder
			: selectedItems.length <= 2
				? selectedItems.map((item) => item.name).join(", ")
				: `${selectedItems.length} selecionados`;

	const filtred = useMemo(() => {
		const normalized = searchTerm.trim().toLowerCase();
		if (!normalized) return itens;
		return itens.filter((item) =>
			String(item.name || "").toLowerCase().includes(normalized)
		);
	}, [searchTerm, itens]);

	useEffect(() => {
		if (!open || menuPlacement !== "inline") return;
		containerRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
	}, [open, menuPlacement]);

	const toggle = (identifier) => {
		if (Array.isArray(identifier)) {
			if (selected.indexOf(identifier[0]) > -1){
				setSelect(prev =>
					prev.filter(item => !identifier.includes(item))
				);
			} else {
				setSelect(prev => [
					...prev,
					...identifier
				]);
			}
		} else {
			if (selected.indexOf(identifier) > -1) {
				setSelect(selected.filter((id) => id !== identifier))
			} else {
				setSelect([...selected, identifier])
			}
		}
	};

	return (
		<div ref={containerRef} className="tailwick-tags-multi">
			<div className="tailwick-tags-multi__dropdown relative">
				<button
					type="button"
					className="form-input form-input-sm tailwick-tags-multi__trigger flex w-full items-center justify-between gap-3 text-left"
					onClick={() => setOpen((current) => !current)}
					aria-expanded={open}
					aria-haspopup="listbox"
				>
					<span
						className={`truncate ${selected.length > 0
								? "text-default-800"
								: "text-default-500"
							}`}
					>
						{summary}
					</span>
					<IconChevronDown open={open} />
				</button>

				{open && (
					<div
						className={clsx(
							"tailwick-tags-multi__menu",
							menuPlacement === "inline" && "tailwick-tags-multi__menu--inline"
						)}
						role="listbox"
					>
						<div className="tailwick-tags-multi__search-wrap">
							<input
								type="search"
								className="form-input form-input-sm tailwick-tags-multi__search"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder={placeholder}
								autoFocus
							/>
							<div className="tailwick-tags-multi__search-icon">
								<svg
									className="size-3.5 text-default-500"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									aria-hidden
								>
									<circle cx="11" cy="11" r="8" />
									<path d="m21 21-4.35-4.35" />
								</svg>
							</div>
						</div>

						<div className="tailwick-tags-multi__list">
							{filtred.length > 0 ? (
								filtred.map((item) => {
									const checked = Array.isArray(item.id)
										? selected.indexOf(item.id[0])  > -1
										: selected.indexOf(item.id) > -1;
									const color = item.color || "#64748b";

									const appearance =
										typeof exibicao === "function"
											? exibicao(item)
											: null;

									return (
										<label key={item.id} className="tailwick-tags-multi__option">
											<input
												type="checkbox"
												className="tailwick-filter-panel__checkbox"
												checked={checked}
												onChange={() => toggle(item.id)}
											/>
											{appearance ? appearance :
											<span
												className="tailwick-filter-panel__color-dot"
												style={{ backgroundColor: color }}
											/>
											}
											<span className="tailwick-tags-multi__option-label">
												{item.name}
											</span>
										</label>
									);
								})
							) : (
								<p className="tailwick-filter-panel__empty">
									{itens.length > 0
										? i18n.t("chat.layout.filterTagsNoResults")
										: i18n.t("chat.layout.filterTagsEmpty")}
								</p>
							)}
						</div>
					</div>
				)}
			</div>

			{selectedItems.length > 0 && (
				<div className="tailwick-tags-multi__selected">
					{selectedItems.map((item) => (
						<Badge key={item.id} item={item} onRemove={toggle} exibicao={exibicao} />
					))}
				</div>
			)}
		</div>
	)
};

export default ModalMultiSelector
